// engine-exception: a conversation is a feed plus a composer, with "make it a ticket" as the one decision on it — the generic detail screen renders columns, not a thread, and has nowhere to type a reply
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAction, useApiQuery } from '../../engine/api/hooks';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Icon } from '../../engine/design/icons';

// The conversation workspace (docs/servicedesk-v2-proposal.md P5). One thread, whatever channel it
// arrived on. The only decision on it is whether it becomes a ticket — and when it does, everything
// already said goes with it, so the customer never repeats themselves.

interface Message {
  id: string;
  seq: number;
  direction: 'in' | 'out' | 'internal' | string;
  author_kind: string;
  author_name: string | null;
  body: string | null;
  ticket_action_id: string | null;
  created_at: string;
}
interface Thread {
  conversation: Record<string, unknown> & { id: string; subject: string | null; state: string; ticket_id: string | null; participant_name: string | null; participant_email: string | null };
  channel: { name: string; kind: string } | null;
  messages: Message[];
}

const stateTone = (s: string) => (s === 'linked' ? 'info' : s === 'deflected' ? 'pos' : s === 'closed' ? 'muted' : 'accent');
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function ConversationScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const thread = useApiQuery<Thread>(['conversation', id], `/v1/conversations/${id}`, { staleTime: 0 });
  const [reply, setReply] = useState('');

  const post = useAction('conversations', 'post', { toast: { success: false } });
  const link = useAction<{ ticket_id: string }>('conversations', 'link', { toast: { success: 'It is a ticket now' } });
  const close = useAction('conversations', 'close', { toast: { success: 'Conversation closed' } });

  if (thread.error) return <ErrorBanner error={thread.error} />;
  if (!thread.data) return <Skeleton lines={6} />;

  const { conversation: conv, channel, messages } = thread.data;
  const closed = conv.state === 'closed' || conv.state === 'deflected';

  const send = async () => {
    const text = reply.trim();
    if (!text) return;
    await post.mutateAsync({ id, body: { body: text, direction: 'out', author_kind: 'agent' } });
    setReply('');
    await thread.refetch();
  };

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
      <PageHeader
        title={conv.subject ?? 'Conversation'}
        subtitle={[channel?.name, conv.participant_name ?? conv.participant_email].filter(Boolean).join(' · ')}
        actions={
          <div className="hf-row" style={{ gap: 'var(--s-1)' }}>
            <Badge tone={stateTone(conv.state)}>{conv.state === 'linked' ? 'On a ticket' : conv.state}</Badge>
            {conv.ticket_id ? (
              <Button variant="ghost" onClick={() => navigate(`/tickets/${conv.ticket_id}`)}>
                <Icon name="ticket" /> Open the ticket
              </Button>
            ) : (
              <Button onClick={() => { void link.mutateAsync({ id }).then((r) => { if (r?.ticket_id) navigate(`/tickets/${r.ticket_id}`); }); }} disabled={link.isPending}>
                <Icon name="ticket" /> Make it a ticket
              </Button>
            )}
            {!closed && (
              <Button variant="ghost" onClick={() => { void close.mutateAsync({ id, body: { reason: 'closed' } }).then(() => thread.refetch()); }} disabled={close.isPending}>
                Close
              </Button>
            )}
          </div>
        }
      />

      {messages.length === 0 ? (
        <EmptyState title="Nothing said yet" hint="When they write, it appears here." />
      ) : (
        <ol className="hf-stack" style={{ gap: 'var(--s-2)', listStyle: 'none', padding: 0, margin: 0 }}>
          {messages.map((m) => (
            <li
              key={m.id}
              className="hf-card"
              style={{
                padding: 'var(--s-2)',
                marginInlineStart: m.direction === 'out' ? 'var(--s-6)' : 0,
                marginInlineEnd: m.direction === 'out' ? 0 : 'var(--s-6)',
                background: m.direction === 'internal' ? 'var(--warn-bg)' : undefined,
              }}
            >
              <div className="hf-row hf-muted" style={{ gap: 'var(--s-1)', fontSize: 'var(--t-12)' }}>
                <strong>{m.author_name ?? (m.author_kind === 'contact' ? 'Customer' : m.author_kind === 'bot' ? 'Assistant' : 'Us')}</strong>
                <span>{when(m.created_at)}</span>
                {/* The badge is the promise the spine makes: this is the ticket's record, not a copy of it. */}
                {m.ticket_action_id && <Badge tone="muted">on the ticket</Badge>}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
            </li>
          ))}
        </ol>
      )}

      {!closed && (
        <div className="hf-stack" style={{ gap: 'var(--s-1)' }}>
          <textarea
            className="hf-input"
            rows={3}
            value={reply}
            placeholder="Reply on the channel they wrote on…"
            aria-label="Reply"
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void send(); }}
          />
          <div className="hf-row" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={() => void send()} disabled={!reply.trim() || post.isPending}>Send</Button>
          </div>
        </div>
      )}
    </div>
  );
}
